// wc-cart-fragments.js n'est pas chargé sur ce site (WooCommerce gère les
// fragments directement dans les réponses add_to_cart/remove_from_cart).
// L'event global "wc_fragment_refresh" n'a donc aucun listener : on
// remplace nous-mêmes les fragments dans le DOM.

import { pushItemEvent, pushCouponEvent, couponCodes } from './data-layer';

// Cart known to the front (bootstrapped server-side, then updated from each
// fragment refresh), so add_to_cart / remove_from_cart only fire for products
// that just appeared / disappeared. See Tracking::cart_payload().
let cart = (window.vsTracking && window.vsTracking.cart) || { items: {}, value: 0, coupon: '' };

export function getCart() {
    return cart;
}

function trackCartChanges(raw) {
    let next;
    try {
        next = JSON.parse(raw);
    } catch (e) {
        return;
    }
    if (!next || !next.items) return;

    Object.keys(next.items).forEach((id) => {
        if (!(id in cart.items)) pushItemEvent('add_to_cart', next.items[id]);
    });
    // The removed line's data comes from the previous cart.
    Object.keys(cart.items).forEach((id) => {
        if (!(id in next.items)) pushItemEvent('remove_from_cart', cart.items[id]);
    });
    // A code missing from the previous cart was just applied (WooCommerce
    // only keeps valid ones).
    const previousCoupons = couponCodes(cart.coupon);
    couponCodes(next.coupon).forEach((code) => {
        if (!previousCoupons.includes(code)) pushCouponEvent(code, next.value, next.discount);
    });

    cart = next;
}

export function applyFragments(fragments) {
    if (!fragments) return;
    Object.keys(fragments).forEach((selector) => {
        document.querySelectorAll(selector).forEach((el) => {
            el.outerHTML = fragments[selector];
        });
    });

    // Not a DOM fragment either: cart content, diffed to emit add_to_cart / remove_from_cart.
    if (typeof fragments['cart-tracking'] === 'string') {
        trackCartChanges(fragments['cart-tracking']);
    }

    // Not a DOM fragment (see Cart::cart_count_fragment): the IDs currently in the
    // cart, plus what the cart already covers (collections, Lifetime).
    if (typeof fragments['cart-product-ids'] === 'string') {
        syncCartButtons(
            fragments['cart-product-ids'].split(',').filter(Boolean),
            parseJson(fragments['cart-covered'], {}),
            parseJson(fragments['cart-cover-all'], null)
        );
    }
}

export function refreshCartFragments() {
    return fetch('/?wc-ajax=get_refreshed_fragments', { method: 'POST' })
        .then((r) => r.json())
        .then((data) => applyFragments(data && data.fragments));
}

// Pas de double ajout : un bouton en cours de requête ou déjà marqué
// "dans le panier" ne doit plus rien déclencher.
export function isBtnLocked(btn) {
    return btn.classList.contains('loading') || btn.classList.contains('is-disabled');
}

function setBtnLabel(btn, text) {
    const label = btn.querySelector('.text-reveal-label');
    if (!label) {
        btn.textContent = text;
        return;
    }

    label.dataset.text = text;
    label.querySelectorAll('.text-reveal-text, .text-reveal-copy').forEach((el) => {
        el.textContent = text;
    });
}

function parseJson(raw, fallback) {
    try {
        return JSON.parse(raw) ?? fallback;
    } catch (e) {
        return fallback;
    }
}

// Grise (ou réactive) chaque CTA d'ajout selon le contenu du panier, partout
// sur la page. data-children = produits que le CTA ajoute (collection), sinon
// data-product_id. data-label = libellé d'origine à restaurer.
// Ce que le panier couvre déjà (libellés calculés côté serveur, voir
// Collections::included_label) :
// - covered : id => "Already in {collection}"
// - coverAll : { except, label } quand le Lifetime est dans le panier, il couvre
//   tout sauf lui-même ("Included with Lifetime Access").
export function syncCartButtons(ids, covered = {}, coverAll = null) {
    document.querySelectorAll('.ajax_add_to_cart[data-product_id], .js-add-grouped-to-cart[data-product_id]').forEach((btn) => {
        const targets = (btn.dataset.children || btn.dataset.product_id).split(',').filter(Boolean);
        if (!targets.length) return;

        // "Download for free" link (page-product.twig): hidden while Lifetime is
        // in the cart, which already includes the free product.
        if (btn.classList.contains('js-free-download')) {
            btn.hidden = !!coverAll;
            if (coverAll) return;
        }

        const includedLabel = coverAll && !targets.includes(String(coverAll.except))
            ? coverAll.label
            : targets.every((id) => id in covered) ? covered[targets[0]] : '';
        const inCart = !!includedLabel || targets.every((id) => ids.includes(id));

        if (btn.dataset.label === undefined) {
            const text = btn.querySelector('.text-reveal-text') || btn;
            btn.dataset.label = text.textContent.trim();
        }

        btn.classList.toggle('is-disabled', inCart);
        btn.toggleAttribute('aria-disabled', inCart);
        btn.classList.toggle('is-included', !!includedLabel);
        setBtnLabel(btn, includedLabel || (inCart ? 'In cart' : btn.dataset.label));
    });
}

// Au chargement : les CTA rendus côté serveur n'ont pas is-disabled, on les
// aligne sur le panier courant (bootstrappé dans vsTracking).
function initCartButtons() {
    const tracking = window.vsTracking || {};
    syncCartButtons(Object.keys(cart.items), tracking.covered || {}, tracking.coverAll || null);
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCartButtons);
} else {
    initCartButtons();
}
